import { motion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";

// Stable card identities — order and image association are fixed by key,
// never by array position, so all languages show the same six cards in
// the same order with the same image.
const ORDER = [
  "firmenevents",
  "branding",
  "vereine",
  "gemeinden",
  "messen",
  "schulen",
];

const IMAGES = {
  firmenevents: "/images/fp-occ-firmenevents.webp", // FP_web_0201 (assigned)
  branding: "/images/fp-branding-1.webp", // FP_web_0901 (moved here per Task 4)
  vereine: "/images/fp-events-2.webp", // reuse of supplied FP_web_0401
  gemeinden: "/images/fp-occ-gemeinden.webp", // FP_web_0101 (assigned)
  messen: "/images/fp-occ-messen.webp", // FP_web_07 (assigned, Task 4)
  schulen: "/images/fp-occ-schulen.webp", // FP_web_13 (assigned)
};

export const Occasions = () => {
  const { t } = useLanguage();
  if (!t.occasions) return null;

  return (
    <section
      id="occasions"
      data-testid="occasions-section"
      className="py-20 md:py-32 bg-[#F8FAFC]"
    >
      {/* Anchor aliases so existing nav links (#corporate, #schools) still resolve */}
      <span id="corporate" aria-hidden="true" className="block relative -top-24" />
      <span id="schools" aria-hidden="true" className="block relative -top-24" />

      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0055FF]">
            {t.occasions.kicker}
          </div>
          <h2 className="mt-3 font-heading text-3xl md:text-5xl font-bold tracking-tight text-neutral-900">
            {t.occasions.title}
          </h2>
          {t.occasions.subtitle && (
            <p
              data-testid="occasions-subtitle"
              className="mt-4 text-lg text-neutral-600 leading-relaxed"
            >
              {t.occasions.subtitle}
            </p>
          )}
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ORDER.map((key, i) => {
            const item = t.occasions.cards[key];
            return (
              <motion.article
                key={key}
                data-testid={`occasions-card-${key}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className="relative overflow-hidden rounded-3xl bg-white border border-slate-200/60"
              >
                <div className="relative aspect-[4/3] overflow-hidden">
                  <img
                    src={IMAGES[key]}
                    alt={item.alt || item.t}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                </div>
                <div className="p-6 md:p-7">
                  <h3 className="font-heading text-xl md:text-2xl font-semibold text-neutral-900">
                    {item.t}
                  </h3>
                  <p className="mt-2 text-neutral-600">{item.d}</p>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Occasions;
